import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/**
 * Domain types the internal version history endpoint looks up by short name —
 * `DomainObjectRegistry` in core, less its deprecated entries. Anything else
 * answers 404.
 */
export const VERSION_HISTORY_TYPES = [
  'AdUnit', 'Advertiser', 'Asset', 'AxProduct', 'Blocklist', 'ContextServiceConnection', 'Coupon',
  'Creative', 'CreativeBundle', 'CreativeCollection', 'CreativeSet', 'CustomEventType', 'DataExport',
  'DiagnosticRequestExecution', 'EarningsAccount', 'Election', 'EmailTemplate', 'EmailTranslation',
  'ExternalAdUnit', 'ExternalDemandSource', 'FieldMappings', 'FirstPartyAudience', 'Folder', 'Folio',
  'InvoiceTranslation', 'Layout', 'LayoutInclude', 'LibraryCreative', 'LineItem', 'LineItemTemplate',
  'Network', 'NetworkData', 'Note', 'NotificationPreferences', 'Order', 'Payment', 'Placement',
  'PoliticalCampaign', 'PoliticalEntity', 'PrintLineItem', 'Product', 'QueryStatsTemplate',
  'ReportComponent', 'ReportExecution', 'ReportSchedule', 'ReportTemplate', 'ReportTranslation',
  'Role', 'Ruleset', 'Site', 'SiteGroup', 'TargetingPackage', 'TargetingTemplate', 'Team',
  'TeamGroup', 'ThirdPartyAudience', 'Tier', 'Trigger', 'User', 'UserSegment', 'Workspace',
] as const

export type VersionHistoryType = (typeof VERSION_HISTORY_TYPES)[number]

/** rui detail routes and the domain type their `:id` names. */
const ROUTE_TYPES: Array<[RegExp, VersionHistoryType]> = [
  [/^\/advertising\/advertisers\/([^/]+)/, 'Advertiser'],
  [/^\/advertising\/orders\/([^/]+)/, 'Order'],
  [/^\/advertising\/lineitems\/([^/]+)/, 'LineItem'],
  [/^\/advertising\/creatives\/([^/]+)/, 'Creative'],
  [/^\/publishing\/sites\/([^/]+)/, 'Site'],
  [/^\/publishing\/site-groups\/([^/]+)/, 'SiteGroup'],
  [/^\/publishing\/ad-units\/([^/]+)/, 'AdUnit'],
  [/^\/publishing\/earnings\/([^/]+)/, 'EarningsAccount'],
  [/^\/publishing\/blocklists\/([^/]+)/, 'Blocklist'],
  [/^\/publishing\/rulesets\/([^/]+)/, 'Ruleset'],
  [/^\/political\/campaigns\/([^/]+)/, 'PoliticalCampaign'],
  [/^\/political\/entities\/([^/]+)/, 'PoliticalEntity'],
  [/^\/political\/elections\/([^/]+)/, 'Election'],
  [/^\/(?:political|admin)\/users\/([^/]+)/, 'User'],
  [/^\/admin\/teams\/([^/]+)/, 'Team'],
  [/^\/admin\/team-groups\/([^/]+)/, 'TeamGroup'],
  [/^\/admin\/layouts\/([^/]+)/, 'Layout'],
  [/^\/admin\/products\/([^/]+)/, 'Product'],
  [/^\/admin\/custom-events\/([^/]+)/, 'CustomEventType'],
  [/^\/admin\/tiers\/([^/]+)/, 'Tier'],
  [/^\/admin\/data-exports\/([^/]+)/, 'DataExport'],
  [/^\/reports\/templates\/([^/]+)/, 'ReportTemplate'],
  [/^\/reports\/schedules\/([^/]+)/, 'ReportSchedule'],
  [/^\/reports\/translations\/([^/]+)/, 'ReportTranslation'],
]

/** The object a detail page shows, so the version history can open on it. */
export function versionHistoryTargetForPath(
  pathname: string
): { type: VersionHistoryType; id: string } | null {
  for (const [pattern, type] of ROUTE_TYPES) {
    const id = pattern.exec(pathname)?.[1]
    if (id && id !== 'new' && id !== 'upload') return { type, id }
  }
  return null
}

const RECENT_OBJECTS_KEY = 'adnuntius.recent-objects'
const RECENT_OBJECTS_MAX = 10

/** A detail page visited lately, for picking it again on the version history page. */
export interface RecentObject {
  type: VersionHistoryType
  id: string
  name: string
}

/**
 * The last objects visited, newest first, per user and network — ids only mean
 * something in their own network. Kept in local storage as a convenience: a
 * blocked or cleared store just leaves the list empty.
 */
export const RecentObjects = {
  list: (scope: string): RecentObject[] => {
    try {
      const all = JSON.parse(localStorage.getItem(RECENT_OBJECTS_KEY) ?? '{}')
      return Array.isArray(all[scope]) ? all[scope] : []
    } catch {
      return []
    }
  },

  record: (scope: string, visit: RecentObject) => {
    try {
      const all = JSON.parse(localStorage.getItem(RECENT_OBJECTS_KEY) ?? '{}')
      const others = RecentObjects.list(scope).filter(
        (item) => item.type !== visit.type || item.id !== visit.id
      )
      all[scope] = [visit, ...others].slice(0, RECENT_OBJECTS_MAX)
      localStorage.setItem(RECENT_OBJECTS_KEY, JSON.stringify(all))
    } catch {
      // Storage unavailable; nothing to remember.
    }
  },
}

export interface VersionUserRef {
  id: string
  name: string
}

/** One saved version of an object. */
export interface VersionEntry {
  timestamp: string
  user: VersionUserRef | null
  /** Only returned to a masquerading user. */
  masqueraderUser: VersionUserRef | null
  /** The object as saved, as a JSON string. */
  json: string | null
  /** The version before this one, when the backend could rebuild it from the stored patch. */
  previousJson: string | null
}

export interface VersionHistoryPage {
  results: VersionEntry[]
  totalCount: number
}

/** The store keeps user ids; the entry may name them as a reference or as a bare id. */
function mapUser(ref: unknown, id: unknown): VersionUserRef | null {
  if (ref && typeof ref === 'object') {
    const r = ref as Record<string, unknown>
    return { id: String(r.id ?? ''), name: String(r.name ?? r.id ?? '') }
  }
  if (id != null) return { id: String(id), name: String(id) }
  return null
}

function mapEntry(r: Record<string, unknown>): VersionEntry {
  return {
    timestamp: (r.timestamp as string) ?? '',
    user: mapUser(r.user, r.userId),
    masqueraderUser: mapUser(r.masqueraderUser, r.masqueraderUserId),
    json: (r.json as string) ?? null,
    previousJson: (r.previousJson as string) ?? null,
  }
}

export const VersionHistoryApi = {
  /** Every saved version of an object in the current network, newest first. */
  getPage: async (
    type: VersionHistoryType,
    id: string,
    page = 1,
    pageSize = 20
  ): Promise<VersionHistoryPage> => {
    const params = new URLSearchParams({
      context: getContext(),
      id,
      type,
      page: String(page),
      pageSize: String(pageSize),
    })
    const res = await apiFetch(`${API_BASE}/internal/versionhistory?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch version history')
    const data = (await res.json()) as { results?: Record<string, unknown>[]; totalCount?: number }
    const results = (data.results ?? []).map(mapEntry)
    return { results, totalCount: data.totalCount ?? results.length }
  },
}
